import React, { useEffect, useState } from "react";
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/react";
import { Heading, Subheading } from "./heading";
import { Text } from "./text";
import { Badge } from "./badge";
import {
  SparklesIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

export const CHANGELOG_STORAGE_KEY = "vb_changelog_last_seen";

export const changelogEntries = [
  {
    date: "2026-10-19",
    title: "Correcciones en Reportes",
    badge: { label: "Corrección", color: "red" },
    items: [
      "El índice de Reportes hablaba siempre del mes en curso, aunque arriba tuvieras elegido «Este año» o «Últimos 12 meses»: la utilidad, los eventos, los leads y el gasto que adelantaba cada reporte no cuadraban con lo que veías al abrirlo. Ahora siguen el período que elegiste, y arriba del índice dice de qué período son las cifras.",
      "«Últimos 12 meses» llegaba hasta el fin del mes en curso, así que el día 19 ya contaba como realizados los eventos del 20 al 31. Ahora la ventana termina hoy.",
      "En Resumen, los eventos del período que todavía no ocurren se contaban como realizados. El cuadro ahora se llama Eventos del período y separa cuántos ya se realizaron y cuántos faltan; Valor y Utilidad de eventos dicen cuánto de esa cifra está por realizar. En la tabla, los eventos por realizar llevan una marca azul.",
      "Lo que el venue se quedaba de un evento cancelado, lo cobrado menos lo reembolsado, no aparecía en ningún reporte. Ahora Estado de resultados lo muestra en su columna Cancelaciones, el mes en que se canceló y descontando lo que ese evento sí gastó, y el Resultado lo suma. En Resumen, los cancelados del período aparecen en la tabla de eventos con lo retenido.",
      "En Resumen, «Por pagar hoy» no cambia al mover el período, y parecía que el filtro fallaba. Es la foto de lo que debes hoy, y ahora lo dice.",
    ],
  },
  {
    date: "2026-10-14",
    title: "Gastos pagados más claros en Cuentas y en cada evento",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "En Cuentas → Balance general, los egresos ya no muestran el botón «Por pagar» junto a cada gasto. Se leía como un estado y hacía parecer pendientes gastos que ya estaban pagados. Todo lo que aparece en el Balance está pagado, y la columna ahora se llama Fecha de pago para que quede claro.",
      "Cada movimiento se corrige donde nació. Los costos de un evento se editan, se regresan a pendiente o se eliminan desde el evento, al que llegas con un clic en su nombre. Los gastos generales del venue, que se capturan en el Balance, ahora también se editan ahí con el ícono de lápiz: concepto, monto, categoría, fecha de pago y método. Si marcaste como pagado uno que todavía debes, edítalo y cámbialo a Por pagar. Los retiros se siguen eliminando desde el Balance.",
      "En el Balance de cada evento, los costos pagados ahora muestran debajo de «Pagado» la fecha en que salió el dinero y el método. Al hacer clic en «Pagado» ya no regresa el costo a pendiente: se abre su pago para que corrijas la fecha o el método, y si de verdad quieres dejarlo pendiente, ahí está «Regresar a pendiente».",
      "El método de pago ahora se ve donde se listan movimientos. Balance general tiene una columna Método para ingresos, egresos, reembolsos y retiros, y Movimientos también la tiene para los depósitos en garantía. En Flujo de caja, los egresos de cada mes lo muestran junto a la categoría. La comisión del planner, en el evento y en su página del directorio, dice cómo se pagó. Los gastos de un servicio ahora piden el método al registrarlos como pagados y dicen en su lista si están pagados y cómo.",
      "Los gastos recurrentes por confirmar ahora se agrupan por mes, con el subtotal de cada uno. Dentro del mes se ordenan por concepto, así que las dos quincenas de un mismo servicio quedan juntas.",
      "Recuerda que un recurrente sin confirmar todavía no cuenta en el neto, el flujo de caja ni los reportes. Confírmalos cada mes para que tus cifras reflejen lo que realmente gastas.",
    ],
  },
  {
    date: "2026-10-13",
    title: "Varios clientes por evento y menos duplicados en el directorio",
    badge: { label: "Mejora", color: "emerald" },
    items: [
      "Un evento ya puede tener varios clientes del directorio, cada uno con el papel que tú le pongas: el novio y la novia, la productora y quien paga. En el evento, en la sección Contactos en este evento, agrega a quienes participan y escribe su rol en la tarjeta. El cliente principal sigue siendo el que carga con el valor y los pagos. Los demás ven el evento en su historial con su rol, sin dinero.",
      "Para cambiar el cliente de un evento ya no hay que desligar y volver a ligar: el botón Cambiar cliente abre el directorio y reemplaza el vínculo en un paso.",
      "Al ligar un cliente, los que coinciden con el correo o el teléfono del evento se muestran todos, al pie de la búsqueda. Si hay dos iguales, los ves juntos y eliges cuál conservar.",
      "Al convertir un lead sin correo ni teléfono, el panel te avisa antes: sin esos datos no se puede ligar solo a un cliente que ya exista.",
      "El asistente del directorio ahora también reconoce a un cliente existente por el nombre de la empresa, aunque esté guardado como persona física.",
      "Todo lead y todo evento nacen con su cliente. Al crear un lead en el panel escribes su nombre y eliges al cliente del directorio, o lo das de alta ahí mismo. Los leads que llegan desde venueboard.com se ligan solos al cliente por su correo o teléfono, o lo dan de alta si es nuevo. Se acabaron los clientes duplicados por capturar a mano.",
      "El formulario de nuevo evento en el calendario cambió: la sección Cliente es un solo bloque donde eliges a quién es el evento y ves su contacto antes de guardar. Ya no hay campos sueltos de nombre, correo y teléfono que recapturar; vienen del cliente.",
      "En cada lead ves a su cliente, lo cambias o ligas uno a los leads viejos que no lo tenían. En el tablero, junto al nombre del cliente aparece un contador con cuántos leads y eventos más tiene. En el directorio hay una columna de leads, y en cada cliente su lista: cuántas veces ha cotizado, en qué quedó cada uno y cuáles se convirtieron.",
      "La página del cliente se limpió: solo se muestran los datos que tiene, los contactos aparecen únicamente en empresas, y las cifras se muestran cuando hay algo que contar.",
      "Al dar de alta un cliente, desde el directorio, un lead o un evento, el panel te avisa si ya tienes a alguien con el mismo correo, teléfono, nombre y apellido o empresa. Puedes usar el que ya existe o crear uno nuevo de todas formas.",
      "El número de eventos de un cliente cuenta también los eventos donde participa sin ser el principal, como la novia o quien paga. El valor y los pagos siguen contando en el cliente principal del evento.",
    ],
  },
  {
    date: "2026-10-13",
    title: "Rol Inversionista: acceso de solo lectura para socios",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Si tienes socios o inversionistas que necesitan seguir el negocio sin operarlo, ahora pueden tener su propia cuenta con el rol Inversionista. Ven todo tu venue —calendario, leads, eventos con su cotización y balance, cuentas, reportes, directorio y servicios— pero no pueden modificar nada. La configuración y la bitácora quedan fuera de su alcance.",
      "Es distinto del Visor: el Visor es para tu equipo operativo y no ve dinero; el Inversionista ve las cifras completas, sin poder capturar ni editar. Pídelo a Venueboard y lo damos de alta con los venues que indiques.",
    ],
  },
  {
    date: "2026-10-11",
    title: "Cómo te ha ido con cada cliente y cada planner",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Cuando un cliente te vuelve a cotizar, quieres saber cómo salieron los anteriores sin abrir evento por evento. Su página en el Directorio ahora dice, además del valor y lo pagado, cuánto te costaron sus eventos y qué utilidad dejaron, con el margen. La tabla de eventos trae Costos y Utilidad por evento y una fila de totales.",
      "Lo mismo para los planners: en su ficha, la sección Eventos abre con los resultados de todo lo que ha traído —valor, cobrado, saldo, su comisión con el estado de pago, costos y utilidad— y la comisión efectiva sobre lo que trajo. Los eventos cancelados se listan con su gasto, pero no entran al margen.",
    ],
  },
  {
    date: "2026-10-11",
    title: "Los depósitos que te quedaste ya cuentan en Reportes",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "Cuando aplicas un depósito en garantía —por daños, una penalización, un no-show— ese dinero es ingreso tuyo, pero hasta hoy ningún reporte lo sumaba. El motivo que escribes al resolverlo queda visible en la tarjeta del depósito. En Estado de resultados aparece en su propia columna, Depósitos aplicados, aparte de la utilidad de los eventos, y el Resultado del mes junta las dos cosas. Las columnas solo se muestran si el año tiene alguno.",
      "En Flujo de caja, al abrir un mes, las aplicaciones se listan junto a los depósitos recibidos y devueltos, marcadas como ingreso: no mueven la cuenta, pero ese día dejaron de ser custodia. Y en Resumen, el indicador de Ingresos dice cuánto entró además por esta vía.",
    ],
  },
  {
    date: "2026-10-11",
    title: "Correcciones",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Leads por atender, en Inicio, mostraba leads que ya habías eliminado del pipeline. Ya no aparecen ahí. Analíticas web y Reportes siguen contando todos los formularios que recibiste, aunque después borres alguno del pipeline: lo que llegó, llegó.",
      "En Cuentas → Movimientos, un depósito devuelto el mismo día en que se recibió podía salir arriba de su propio registro de entrada. Ahora el orden es el real: primero recibido, después su desenlace. Las devoluciones parciales se marcan como tales y dicen cuánto se aplicó.",
      "Movimientos ahora muestra también al proveedor, no solo al evento, en los depósitos y gastos que vienen de uno. En Cuentas → Depósitos en garantía, los nombres largos ya no rompen la tabla en varias líneas.",
      "En los reportes, los costos dejaron de pintarse en rojo: son una serie más, no una alarma. El rojo queda para lo que sí es un problema, como la cartera vencida.",
      "Un lead marcado como Perdido seguía apartando sus fechas de interés: pintaba el calendario de leads y salía como conflicto al revisar las fechas de otro lead. Ya no: perder un lead libera sus fechas, igual que eliminarlo o convertirlo en evento.",
    ],
  },
  {
    date: "2026-10-11",
    title: "Resolver un depósito: aplicar, devolver o repartir",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "Los depósitos en garantía, tanto los del cliente en el evento como los del proveedor en su ficha, ya no tienen dos botones. Hay uno: Resolver. Al abrirlo decides cuánto se queda el venue —0, 25, 50, 75 o 100 por ciento, un porcentaje libre o un monto en pesos— y ves el reparto antes de guardar: cuánto se aplica y cuánto se devuelve.",
      "Lo que se devuelve lleva su fecha, método y comprobante como siempre; lo que se aplica cuenta como ingreso en Cuentas desde ese día. Antes, para quedarte con la mitad tenías que calcular el monto a mano y registrarlo como devolución.",
      "Las confirmaciones de depósitos y comprobantes dejaron de usar la ventana del navegador: ahora son los mismos diálogos que el resto de la plataforma, con las cifras a la vista.",
    ],
  },
  {
    date: "2026-10-11",
    title: "Un proveedor, varias categorías",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "El proveedor que te hace la decoración y también renta el mobiliario ya no tiene que escoger. Al darlo de alta, y en su ficha, puedes agregarle todas las categorías que le queden, cada una con su subcategoría.",
      "No hay categoría principal: todas pesan igual. Un planner que además renta mobiliario aparece en la pestaña Planners y en la de Proveedores, su ficha muestra tanto su acuerdo de comisión como sus servicios, y sus servicios generan costo en el evento como los de cualquier proveedor. La comisión nace solo cuando lo asignas como planner.",
      "En el Directorio, la columna de categoría muestra todas y los filtros lo encuentran por cualquiera de ellas.",
    ],
  },
  {
    date: "2026-10-11",
    title: "Operación y Balance, cada uno en su pestaña",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "La pestaña Balance del evento cargaba con todo: planner, proveedores, servicios, cobros, costos y depósitos en un solo recorrido. Ahora son dos. Operación responde quién participa y qué entrega cada quien: planner, proveedores asignados y servicios. Balance se queda con el dinero: cuánto vale el evento, cuánto has cobrado, costos, depósitos en garantía y reembolsos.",
      "Las dos pestañas se apuntan entre sí, porque los costos que ves en Balance nacen de los proveedores y servicios de Operación. Tus links guardados siguen funcionando: los que abrían Proveedores caen en Operación.",
    ],
  },
  {
    date: "2026-09-26",
    title: "Tus rentas ya saben qué incluyen",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "En Servicios, una renta —la del salón, la del viernes de montaje, la del domingo— ahora es un tipo propio y lleva su lista de lo que trae: sillas, mesas, baños, personal de montaje, limpieza. Solo cabe dentro lo que ya tienes marcado como incluido en la renta; lo que se cobra aparte, como una ambulancia, sigue siendo un servicio aparte.",
      "Al cotizar una renta, sus incluidos aparecen todos pre-marcados y entran a la cotización como renglones propios en $0, con su cantidad. Puedes desmarcar lo que ese evento no lleva. El precio de la renta no cambia por desmarcar: si negociaste menos, va como descuento. En el PDF cada incluido sale sangrado bajo la renta, con la palabra «Incluido» en lugar de precio.",
      "Si un incluido lo da un proveedor —la limpieza subcontratada—, su costo se registra solo en el evento igual que cualquier servicio de tu catálogo, aunque al cliente no se le cobre. Así la utilidad del evento sale completa.",
      "El asistente de Servicios tiene una pasada nueva: te enseña los servicios marcados como incluidos que no viven en ninguna renta y te pregunta en cuáles van. Si tienes incluidos pero ninguna renta dada de alta, te lo dice y te deja crearla ahí mismo.",
    ],
  },
  {
    date: "2026-09-26",
    title: "Piezas por cantidad: 240 sillas sin darlas de alta una por una",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Un servicio con piezas ahora se administra de dos formas. Identificadas una por una, como hasta hoy, para calentadores o luces que tienen nombre y estado. O por cantidad, para sillas, mesas y todo lo que da igual cuál sale: capturas el total, y aparte lo que está fuera de circulación —en mantenimiento, en otra bodega, prestado— con el nombre que tú le pongas. Lo que queda es lo que puedes apartar.",
      "Al apartar piezas por cantidad para un evento, ves cuántas quedan libres en esas fechas antes de capturar, y no se puede apartar más de las que hay. Si dos personas apartan al mismo tiempo, la segunda ve lo que la primera tomó. Mover el evento de fecha vuelve a revisar el cupo, y cancelarlo lo libera.",
      "En la ficha del servicio, pestaña Reservas, hay un calendario de cuatro meses con la disponibilidad día por día: verde es lo libre, y se vacía conforme se compromete. Pasa el cursor o el teclado por un día para ver quién tiene qué. Si un día quedó comprometido de más —porque bajaste el total después de apartar— se marca en rojo y te lo dice.",
      "En la sección de servicios del evento aparece un aviso ámbar cuando la cotización lleva piezas que todavía no están apartadas, con un botón para apartarlas de un click. Y al cotizar, el campo de cantidad te dice cuántas piezas tienes y no deja registrar más de las que existen.",
    ],
  },
  {
    date: "2026-09-23",
    title: "Lo que ya vendiste, mes por mes hacia adelante",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Reporte nuevo: Agenda vendida. Si tienes el calendario cerrado hasta el año que entra, aquí lo ves de un vistazo —cuánto traes contratado, para qué meses y hasta cuándo llega tu agenda— sin tener que entrar al Estado de resultados y cambiarle el año a mano.",
      "Cada mes se parte en lo que ya cobraste y lo que te falta por cobrar de esos mismos eventos, y puedes abrir el mes para ver evento por evento con su cliente, su valor y su saldo. Los meses sin nada vendido aparecen igual, en blanco: un hueco en la agenda es justo lo que conviene ver a tiempo.",
      "No verás utilidad en este reporte, y es a propósito: los costos de un evento que todavía no ocurre no están capturados, así que cualquier utilidad saldría de más. La utilidad de cada evento aparece en el Estado de resultados el mes en que el evento pasa.",
    ],
  },
  {
    date: "2026-09-23",
    title: "Por cobrar y Por pagar, una junto a la otra",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "En Cuentas, la pestaña Deudas ahora se llama Por pagar, y se movió al segundo lugar, junto a Por cobrar. Son las dos caras de la misma pregunta —quién te debe y a quién le debes— y ahora se leen seguidas, con el mismo nombre de un lado y del otro. Balance general quedó después, ya como resumen.",
      "Tus links guardados a Deudas siguen funcionando: te llevan a Por pagar.",
    ],
  },
  {
    date: "2026-09-23",
    title: "Proveedores y servicios ya no esconde tu gasto",
    badge: { label: "Corrección", color: "red" },
    items: [
      "El reporte de Proveedores y servicios solo contaba los costos que traían un proveedor de tu directorio. Si capturas tus gastos escribiéndolos a mano —\"Fotógrafo\", \"Personal\", \"Mamparas\"—, que es lo normal, el reporte te abría vacío aunque en el mes hubieras gastado cientos de miles de pesos. Ya está arreglado: ese gasto ahora aparece agrupado en «Sin proveedor asignado», con su barra y su total, y al abrirlo ves el concepto y el evento de cada costo.",
      "El proveedor se pega solo cuando el costo nace de un servicio de tu catálogo o de una comisión de planner; todo lo demás cae en ese grupo. El reporte ahora lo dice al pie, para que sepas qué hacer si quieres verlo repartido por proveedor.",
      "Al abrir un renglón, cada costo se identifica por su concepto y el evento del que salió. Antes un evento con seis costos daba seis líneas con el mismo nombre del evento repetido, y no se distinguía cuál era cuál.",
      "También cambió el dato que acompaña al reporte en el índice: decía lo que debes de siempre, una cifra que adentro no aparecía por ningún lado. Ahora dice tu gasto del mes, que es justo lo que el reporte enseña al abrirlo. Lo que debes sigue en Cartera y cobranza y en Cuentas → Por pagar.",
    ],
  },
  {
    date: "2026-09-22",
    title: "El historial de tu cliente, evento por evento",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "En la página de un cliente, cada evento de su historial se abre con un click: los conceptos que le cobraste con su cantidad y su precio, los costos de ese evento, los proveedores que trabajaron en él y el planner que lo trajo, con su comisión. Antes, para contestar \"¿qué le cobramos la vez pasada?\", había que entrar evento por evento y regresar.",
      "Al abrirlo ves las cuatro cifras que dicen si fue negocio: valor, costos, utilidad y margen. Es la misma cuenta que ya vive dentro del evento, ahora junto a todos los demás eventos de ese cliente.",
      "Las fechas del historial traen el día de la semana — \"sáb 8 dic 2026\" —: cotizar un sábado no es lo mismo que cotizar un martes, y ahí se lee sin abrir el calendario.",
      "Es solo lectura: para cambiar la cotización, un costo o un proveedor entras al evento, como siempre.",
    ],
  },
  {
    date: "2026-09-21",
    title: "Cinco reportes nuevos",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Configuración se reorganizó en pestañas —Venue, Cotización, Métodos de pago y Archivos— y la ficha de tu venue se mudó ahí, a la pestaña Venue. Por eso desapareció del menú lateral: es la misma de siempre, ahora junto al resto de lo que se configura. Tus links guardados siguen funcionando.",
      "Reportes estrena un índice a la izquierda, agrupado por tema, con el dato vivo de cada reporte debajo de su nombre: cuánto llevas vencido, cuánto del mes está ocupado. El reporte que estés viendo viaja en la liga, así que puedes mandarla tal cual se ve.",
      "Clientes: quién te deja más dinero, quién repite y cuánto te deben. Y te dice de frente cuántos eventos del período no tienen cliente ligado, porque esos no cuentan en ninguna cifra del reporte.",
      "Cartera y cobranza: lo que te deben repartido por antigüedad —por vencer, de 1 a 30 días, de 31 a 60, de 61 a 90 y más de 90— y lo mismo con lo que debes. Un saldo de hace cuatro meses ya no se ve igual que uno que vence la semana que entra.",
      "Estructura de costos: cuánto te cuesta abrir aunque no vendas. Tu gasto del período partido en lo fijo, lo que cuelga de eventos y lo general, con doce meses de tendencia.",
      "Planners y comisiones: cuánto negocio te trae cada planner y a qué costo, con lo que sigue por pagar y la comisión efectiva sobre lo que trajo.",
      "Ocupación y espacios: qué tan lleno estuviste, qué días de la semana concentran tus eventos y —si manejas varios espacios— cuál sostiene el negocio.",
      "Uso del panel: quién de tu equipo entra, en qué secciones trabaja y cuándo lo hizo por última vez. Si alguien no ha entrado, aparece igual: por eso sirve.",
    ],
  },
  {
    date: "2026-09-21",
    title: "Arma tu directorio de clientes con los eventos que ya hiciste",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Si llevas años operando, tu directorio de clientes abrió vacío: el cliente se crea solo de aquí en adelante, pero todo lo que sabes de tus clientes anteriores quedó guardado dentro de cada evento. Hay un asistente que lo arma al revés, desde tus eventos.",
      "Lo primero que te muestra es quién te ha repetido: junta los eventos que comparten correo o teléfono, y un grupo con dos o más es un cliente que ya volvió contigo. Esos pasan uno por uno, con sus eventos a la vista y la ficha prellenada con lo que traen entre todos.",
      "Los clientes con un solo evento se crean todos juntos en una tabla, porque ahí no hay nada que decidir. Lo que corrijas se escribe también sobre sus eventos, para que la ficha y el evento digan lo mismo.",
      "No junta a nadie sin avisarte: si varios eventos comparten el teléfono de un planner que reservó para clientes distintos, te lo marca y desmarcas los que no sean. Y si alguien ya tiene ficha, te ofrece ligarle sus eventos en vez de duplicarla.",
    ],
  },
  {
    date: "2026-09-17",
    title: "La cotización calcula, y tu venue tiene Directorio",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "La cotización ya no te hace sacar la cuenta: eliges el servicio de tu catálogo, capturas la cantidad —13 horas, 80 personas— y el precio sale del que tienes registrado, multiplicado por esa cantidad. Cuando negociaste distinto, ajustas esa línea a mano.",
      "Si un servicio tiene variantes, la eliges ahí mismo; y si la que necesitas no existe —una renta de 12 horas que nunca habías cotizado— la creas desde la cotización y queda guardada para la próxima vez.",
      "Proveedores ahora se llama Directorio y junta a las tres personas con las que trabaja tu venue: proveedores, planners y clientes, cada quien en su pestaña. Tus links a proveedores siguen funcionando.",
      "La ficha del proveedor se adelgazó: en una sola pantalla ves quién es y cómo contactarlo, qué servicios da, cuánto le has pagado, cuánto le debes, sus depósitos en custodia y sus eventos.",
      "Tus clientes ya existen como tal: al crear un evento eliges uno del directorio y los datos se llenan solos. Su ficha muestra todos sus eventos, cuánto valen, cuánto ha pagado y cuánto debe, y desde ahí generas un lead nuevo cuando te pide otra fecha. Los eventos que ya tenías se te ofrecen para ligarlos si coinciden por correo o teléfono.",
      "Un planner no cobra tarifa, cobra comisión. Registras el acuerdo —monto fijo o porcentaje del valor del evento— con historial de cambios; al ligarlo a un evento se te propone la comisión que toca y queda como costo de ese evento. Cambiar el acuerdo no mueve los eventos anteriores.",
      "Gastos recurrentes: la renta, la limpieza general, la fumigación se configuran una vez con su monto y cada cuánto se repiten, y el panel te los propone cuando toca. Nada entra a tus cifras hasta que lo confirmes.",
      "La página del evento pasó de siete pestañas a cuatro: General, Cotización, Balance y Seguimientos. Balance se lee de arriba abajo —cuánto vale el evento, cuánto has cobrado, cuánto te falta, cuánto cuesta y qué te queda— y cada total aparece una sola vez. Tus links siguen funcionando.",
    ],
  },
  {
    date: "2026-09-05",
    title: "Ahora sabes cuánto te deja cada evento",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Hasta hoy el panel solo medía lo que entra: el valor del evento, los pagos y los depósitos. Ya puedes capturar también lo que sale, y ver de un vistazo si el evento fue negocio: valor menos costos igual a utilidad, con su margen en porcentaje.",
      "Cada servicio que te da un proveedor lleva su tarifa: al asignarlo a un evento eliges qué le contrataste y su costo se captura solo, como \"por pagar\". Si más adelante le sube la tarifa, los eventos anteriores no se mueven — quedaron al precio que acordaste entonces.",
      "Cuando una línea de tu cotización sale de un servicio que da un proveedor, ves cuánto le cobraste al cliente por eso, cuánto te costó a ti y cuánto te quedó. Ideal para lo que subcontratas y revendes.",
      "Cuentas se reordenó en cinco pestañas: Por cobrar, Balance general, Deudas, Movimientos y Depósitos en garantía. Balance general junta tus ingresos y tus egresos del período —ahí registras también los gastos del venue que no cuelgan de ningún evento— y te dice cuánto hay en la cuenta y cuánto de eso está comprometido en depósitos. Tus links siguen funcionando.",
      "Sección nueva en el menú: Servicios, el catálogo de todo lo que puedes poner en una cotización, con lo que cobras y lo que te cuesta en un solo lugar. Tus conceptos de siempre ya viven ahí, tal cual estaban.",
      "Dar de alta un servicio son dos preguntas, las dos opcionales: ¿lo da un proveedor?, ¿tiene piezas físicas que administrar? Los precios son un número de cada lado —lo que cobras por unidad y lo que te cobran—, y lo que no quepa ahí lo ajustas en la línea de la cotización. Un servicio puede tener variantes: la renta de salón en martes, en sábado o con luces es un solo renglón con tres precios.",
      "Si tienes varias piezas del mismo servicio, les das nombre una por una (\"Calentador 03\") y las creas en lote. Tú decides cómo se mide su estado: por palabra, por porcentaje o nada. Y al asignarlas a un evento, las que ya estén comprometidas en otro que se cruza en fechas aparecen bloqueadas: no se puede prometer el mismo calentador dos veces.",
      "Si compraste algo, registra cuánto costó y el panel te contesta cuánto llevas recuperado, cuánto falta y en qué evento se pagó solo — descontando lo que costó usarlo, no solo lo que cobraste.",
      "Movimientos es el estado de cuenta de tu venue: cada pago, reembolso, gasto, comisión de planner, depósito recibido o devuelto y retiro, del más reciente al más viejo. Solo aparece dinero que de verdad se movió: lo que debes y no has pagado vive en Deudas hasta que lo pagues.",
      "Y cuando saques dinero del negocio, regístralo como retiro desde Balance general. Un retiro no es un gasto: baja tu saldo de caja sin tocar la utilidad de tus eventos ni ningún reporte de resultados.",
      "Para verlo todo junto, estrenamos Reportes en el menú: el resumen del período, la rentabilidad evento por evento, el estado de resultados mes a mes, el embudo comercial, el gasto por proveedor con la recuperación de tus inversiones, y tus piezas y equipo. Cada reporte se acota al período que quieras y compara contra el anterior.",
    ],
  },
  {
    date: "2026-09-04",
    title: "Acomoda tu menú a tu gusto",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nuevo lápiz arriba del menú lateral: actívalo, arrastra las secciones al orden que prefieras y dale a la palomita. Tu panel se acomoda a cómo trabajas tú.",
      "El orden se guarda en tu cuenta, así que lo verás igual en cualquier dispositivo donde entres. ¿Te arrepientes? La flechita de restablecer lo regresa al orden original.",
      "De paso, la sección \"Venues\" ahora se llama \"Venue\" — y te lleva directo a la ficha del venue que tengas seleccionado arriba, sin pasar por listas intermedias. Si manejas varios venues, cambiar la selección estando en la ficha te lleva a la del venue nuevo, igual que en el resto de las secciones.",
      "En celular, el contenido ahora aprovecha mucho mejor el ancho de la pantalla: quitamos los márgenes dobles que dejaban casi una cuarta parte de la pantalla vacía a los lados.",
      "En la vista anual del calendario, el detalle de un día ahora se agrupa por evento: una boda que toma cuatro espacios es una sola fila con sus espacios como etiquetas, no cuatro filas repitiendo el mismo nombre.",
    ],
  },
  {
    date: "2026-09-01",
    title: "Tus espacios, ahora en tu página pública",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Cuando tienes espacios o galerías publicadas, tu página en venueboard.com se reorganiza en pestañas — General, Espacios, Eventos y recorrido 3D — para que tus clientes encuentren cada cosa sin recorrer una página eterna. Si no tienes, tu página sigue idéntica a la de siempre.",
      "Cada espacio estrena su propia página con dirección web propia: sus fotos, capacidad, superficie, amenidades y descripción. Puedes mandarle a un cliente el link del salón exacto que le interesa — y Google indexa cada espacio por separado: más puertas de entrada a tu venue.",
      "La pestaña de Eventos la hicimos para que presumas lo que ya lograste: ahí viven las galerías de eventos que publiques, para que un cliente que llega a tu página vea eventos reales celebrados en tu venue — la mejor prueba de lo que sabes hacer.",
      "Si tus espacios se rentan por separado, tu página pública muestra la disponibilidad por espacio: un evento en el jardín ya no marca ocupado el salón. Y en el buscador de venueboard.com, el filtro por número de invitados usa la capacidad real de tus espacios. ¿Te interesa que tus espacios se renten por separado? Escríbenos y lo activamos para tu venue.",
      "En el calendario de este panel, nueva vista de Carriles: el mes con un renglón por espacio, para ver de un vistazo qué salón trabaja cada día y dónde hay huecos.",
      "¿Ya tenías eventos en el calendario? Un asistente te ayuda a decirle a cada uno qué espacios ocupa — uno por uno o todos de una sola vez — para que tu disponibilidad por espacio quede exacta desde el primer día. Mientras no decidas, cada evento sigue apartando el venue completo, como siempre.",
      "En tus leads: filtra el tablero por espacio y ve en cada tarjeta qué espacios pide cada quien.",
      "Protección nueva: ya no se puede eliminar un espacio con eventos futuros — te mostramos cuáles son y te sugerimos desactivarlo en su lugar, para que ninguna fecha reservada quede al aire.",
    ],
  },
  {
    date: "2026-09-01",
    title: "Presume tus eventos: galerías en tu página pública",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva sección \"Galerías de eventos\" en la ficha de tu venue: arma galerías de eventos que ya hiciste, con fotos, tipo de evento, número de invitados y mes en que fue.",
      "Liga cada galería a los espacios donde ocurrió. Un cliente viendo tu salón en Venueboard verá eventos reales celebrados ahí — la mejor prueba de lo que se puede hacer en tu espacio.",
      "Tú decides qué se ve: las galerías nacen como borrador y solo se publican cuando tú lo marcas. Tus eventos del calendario, clientes y proveedores nunca se publican — esto es una vitrina aparte que tú curas.",
      "La fecha solo muestra mes y año, nunca el día exacto del evento.",
      "Cada galería puede llevar hasta 3 links de posts de Instagram de ese evento: en tu ficha pública aparece un botón para verlos ahí mismo, sin afectar la velocidad de tu página.",
    ],
  },
  {
    date: "2026-09-01",
    title: "Casi el doble de visitas en venueboard.com",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "Las últimas semanas trabajamos a fondo el posicionamiento de venueboard.com en Google — nuevas páginas por ciudad y por tipo de evento, contenido del blog y mejoras técnicas — y las visitas al sitio casi se duplicaron.",
      "Más visitas son más ojos en tu venue y más leads en tu tablero de Formularios. Recuerda responder pronto: un lead atendido el mismo día vale oro.",
    ],
  },
  {
    date: "2026-08-17",
    title: "Arreglada la carga lenta en Safari y iPhone",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Si usas Safari o entras desde un iPhone o iPad, este panel de administración podía tardar hasta medio minuto en mostrar tu información — el nombre de tu venue, tus eventos y tus datos aparecían mucho después de abrir la página. Ya está arreglado — ahora carga en segundos, igual que en Chrome.",
      "Esto solo afectaba este panel: la página pública de tu venue en venueboard.com siempre cargó bien y tus clientes nunca lo notaron.",
      "No tienes que hacer nada: con recargar la página una vez, queda. En Chrome y Firefox todo sigue funcionando exactamente igual que siempre.",
    ],
  },
  {
    date: "2026-08-15",
    title: "Da de alta tus espacios: salones, jardines y terrazas",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Tu venue ya no tiene que ser un solo bloque: en la ficha de tu venue, sección \"Espacios y salones\", puedes dar de alta cada espacio por separado — con su propia capacidad (sentados y de pie), superficie, fotos, amenidades y descripción en español e inglés.",
      "¿Un salón grande que se divide en dos? Márcalo como espacio compuesto y elige sus partes. Así queda registrado que rentar el grande es rentar sus partes, y al revés.",
      "Si dos espacios no pueden operar al mismo tiempo — comparten accesos, ruido o estacionamiento — márcalos como incompatibles en la nueva tabla de pares. Y si de plano nunca rentas dos eventos a la vez, hay un interruptor para bloquear todo el venue con cualquier evento, sin configurar nada más.",
      "El calendario ahora entiende de espacios: al crear o editar un evento eliges qué espacios ocupa, y el aviso de cruce de fechas se volvió inteligente — dos eventos en espacios independientes ya no chocan, pero rentar el salón completo sí choca con un evento en una de sus partes, y te decimos exactamente qué espacio causa el cruce.",
      "Filtra el calendario por espacio para ver la agenda de cada salón por separado. Tus eventos de siempre siguen bloqueando el venue completo, tal como estaban.",
      "Ordena tus espacios como quieres que aparezcan, y desactiva temporalmente los que no estén disponibles.",
      "Si no das de alta espacios, no cambia nada: tu venue sigue funcionando exactamente como hoy.",
    ],
  },
  {
    date: "2026-08-15",
    title: "La cotización ahora manda en tus saldos",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "Cuando un evento tiene cotización, los saldos, el estado de pago y el \"Por cobrar\" se calculan con el total de la cotización — ya no con un precio acordado que se quedó viejo. Si actualizas la cotización, tus números se actualizan solos, incluso en eventos que ya tenías.",
      "Al agregar o quitar conceptos de la cotización (o prender el IVA), el precio acordado se actualiza automáticamente al nuevo total. Se acabó capturarlo dos veces.",
      "Ahora te avisamos de las incongruencias en lugar de esconderlas: si un cliente pagó más que el valor del evento verás \"Sobrepago\" con el monto exacto, y si un evento tiene pagos pero ningún valor definido, aparece marcado en tu lista de cuentas — antes esos casos se mostraban como \"Pagado\" o desaparecían de la vista.",
      "En el evento y en Cuentas ahora se muestra el \"Valor del evento\", con una nota de \"(según cotización)\" cuando el número viene de ahí, para que siempre sepas de dónde sale cada cifra.",
    ],
  },
  {
    date: "2026-08-14",
    title: "El inicio ahora te dice qué sigue",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "El resumen de inicio ahora muestra tus próximos 5 eventos con su fecha, cuánto falta (\"hoy\", \"mañana\", \"en 12 días\") y el estado del pago. Un clic te lleva directo al evento.",
      "También ves tus leads por atender: los formularios en Pendiente más recientes, con su antigüedad y el valor de la cotización si ya la armaste. Si alguno lleva más de 3 días esperando respuesta, te lo marcamos en ámbar.",
      "Nueva sección de cobros pendientes: los eventos que aún te deben, con su saldo exacto y los vencidos primero — el número \"Por cobrar\" ahora tiene nombres y apellidos. Un clic te lleva al evento para dar seguimiento.",
      "Nueva sección de ocupación: qué porcentaje de tus días tiene al menos un evento, mes por mes durante los próximos 12 meses — como los eventos se reservan con anticipación, ves qué tan lleno va el año que viene, no solo hoy. Los eventos de varios días cuentan todos sus días.",
      "La ocupación se puede filtrar por tipo de día: todos, fin de semana o entre semana. En fin de semana contamos como piensas tú: si 3 de los 5 fines de semana de octubre tienen evento, eso es 60% — un fin de semana (viernes a domingo) cuenta ocupado con que uno de sus días tenga evento.",
      "Haz clic en cualquier mes de la gráfica y se abre su calendario: los días con evento en verde, hoy marcado, y un acceso directo a tu calendario completo.",
      "Además: ocupación por día de la semana comparando los últimos años con eventos — incluyendo los futuros ya reservados — y el acumulado por año.",
    ],
  },
  {
    date: "2026-08-14",
    title: "Analíticas web renovadas: % de contacto, embudo y comparativas",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva métrica: % de contacto. De todas las visitas a tu página, qué porcentaje terminó en un contacto — un formulario o un clic a tu WhatsApp, Instagram o sitio web. Es el número que dice si tu página está convirtiendo visitas en conversaciones.",
      "Cada métrica ahora se compara contra el periodo anterior: si estás viendo 30 días, ves cuánto subió o bajó vs. los 30 días previos, con su flecha verde o roja.",
      "Nuevo embudo de contacto: visitas → clics a redes → formularios, en una sola gráfica. De un vistazo ves dónde se te van los interesados.",
      "Las gráficas de visitas ya no se saltan los días sin visitas — ahora ves la línea de tiempo completa, con sus ceros. En el periodo \"Todo\" se agrupa por mes para que no se sature.",
      "Cada red social tiene ahora su propio color (Instagram rosa, WhatsApp verde, Sitio Web azul) en todas las gráficas, y cualquier gráfica se puede ver también como tabla.",
      "La pestaña en la que estás (Resumen o Analíticas) se guarda en la dirección de la página: si recargas o compartes el link, se abre justo donde estabas.",
    ],
  },
  {
    date: "2026-08-14",
    title: "Arreglada la pantalla en blanco al editar tu venue",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Si tu navegador traduce la página automáticamente (por ejemplo Google Translate en Chrome), editar la información de tu venue podía dejar la pantalla en blanco al escribir la primera letra. Ya está arreglado — puedes editar con la traducción activada.",
      "Además, si algo llegara a fallar en el app, ya no verás una pantalla blanca sin salida: aparece un aviso con un botón para recargar y seguir donde ibas, sin perder lo guardado.",
    ],
  },
  {
    date: "2026-08-08",
    title: "Tu venue ahora se ve completo en inglés",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "La página de tu venue en inglés ya está completa: descripción, ubicación y documentos. Un cliente que llega buscando en inglés ve todo en su idioma, sin frases sueltas en español a medio camino.",
      "Ya revisamos venue por venue: lo importante de todos los venues publicados está traducido. No tienes que hacer nada para ponerte al día.",
      "Tus documentos ahora llevan dos nombres: el de siempre en español y uno en inglés. Al subir un documento nuevo te vamos a pedir los dos — son dos segundos y evita que tu \"Reglamento\" aparezca en español en la página en inglés.",
      "Los nombres más comunes se llenan solos. Si escribes \"Contrato\", \"Reglamento\", \"Planos\" o \"Press Kit\", el nombre en inglés aparece prellenado; solo lo escribes tú cuando el documento tiene un nombre propio de tu venue.",
      "Puedes subir una versión en inglés de cada documento. Si tienes un brochure en español y otro en inglés, ahora viven en el mismo documento y cada página sirve el archivo que le toca. Si subes solo uno, se usa en los dos idiomas — no tienes que duplicar nada.",
    ],
  },
  {
    date: "2026-08-06",
    title: "Pipeline de leads renovado: búsqueda, filtros y tablero más amplio",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nuevo buscador en el pipeline: escribe un nombre, teléfono, email, tema o parte del mensaje y el tablero se filtra al instante. No importa si escribes con o sin acentos — \"cotizacion\" encuentra \"Cotización\".",
      "Filtros junto al buscador: por origen (Instagram, Referido, WhatsApp, los que tú uses), por fecha de creación (últimos 7, 30 o 90 días, este año) y orden dentro de las columnas (más recientes, más antiguos o mayor valor de cotización). Cuando hay filtros activos ves cuántos leads coinciden del total y un botón para limpiar.",
      "Los filtros se conservan al entrar al detalle de un lead y regresar — sigues exactamente donde estabas, ideal cuando manejas muchos leads.",
      "Cards rediseñadas y más completas: ahora muestran teléfono y email con iconos, el mensaje y las notas en dos líneas, el valor de la cotización en verde junto al nombre, y la antigüedad del lead (\"hace 3 días\") en lugar de solo la fecha.",
      "Columnas con encabezado al tono de su color y el total de cotizaciones completo debajo del nombre de la columna.",
      "El tablero ahora usa todo el ancho y alto de la pantalla — se acabaron los márgenes gigantes; caben más columnas y más leads a la vista.",
    ],
  },
  {
    date: "2026-07-10",
    title: "Crear evento: por defecto cubre todo el día",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "Al crear un evento o bloqueo nuevo, ahora arranca cubriendo el día completo por defecto: Inicio a las 00:00 y Fin a las 23:59, en vez de quedar a mediodía. Bloquear un día entero es de un clic; si necesitas otro horario, lo ajustas como siempre.",
      "Arreglamos un detalle visual en los selectores de hora del evento: el \"00\" se veía cortado y ahora se muestra completo.",
    ],
  },
  {
    date: "2026-07-10",
    title: "Calendario: el mes del encabezado se corrige según la zona horaria",
    badge: { label: "Corrección", color: "red" },
    items: [
      "En el calendario, el título del mes podía mostrar el mes anterior (por ejemplo \"Junio\" mientras la grilla mostraba Julio) cuando abrías el app desde un dispositivo con una zona horaria al oeste de tu venue — por ejemplo si tu equipo está configurado en horario del Pacífico o Baja California. Las fechas de los eventos y el día de hoy siempre estuvieron bien; solo era la etiqueta del mes.",
      "Ahora el título del mes se calcula con la zona horaria de tu venue, así que se ve correcto sin importar dónde esté configurado el dispositivo desde el que entras.",
    ],
  },
  {
    date: "2026-06-09",
    title: "Pipeline de leads: arreglos y notas editables",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Mover leads entre columnas funciona estable de nuevo, sin trabarse.",
      "Cada columna ahora se desliza por separado cuando tiene muchos leads, y puedes ver de un vistazo cuándo hay más abajo. Cuando tienes muchas columnas también puedes deslizarlas de lado para verlas todas.",
      "Las columnas vacías se hacen finitas para no ocupar espacio, pero sirven igual para soltar un lead encima.",
      "Después de mover un lead, la página te lleva hasta él y lo resalta unos segundos para que sepas dónde quedó.",
      "El campo Notas del lead ya se ve y se puede editar desde su página de detalle. Antes solo se podía escribir al crearlo.",
    ],
  },
  {
    date: "2026-06-09",
    title: "Archivos por evento, comprobantes en pagos y depósitos, documentos libres del venue",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva pestaña Archivos dentro de cada evento. Sube cualquier documento interno — cotizaciones firmadas, contratos, recibos, comprobantes — con el nombre y tipo que quieras. Solo tú y los admins ven estos archivos; el viewer no.",
      "Los pagos, reembolsos y depósitos en garantía ahora muestran un chip de comprobante en cada fila/card. Adjuntas el PDF o la imagen del comprobante en el momento de registrar el movimiento o después desde la fila. Doble registro en los depósitos: uno del cobro original y otro de la devolución (este último solo cuando marcas el depósito como devuelto).",
      "Lo mismo en los depósitos de proveedor desde la página del proveedor: comprobante del pago y, si registraste la devolución, comprobante de devolución.",
      "Arrastra y suelta en todos los uploaders del app. Tomas el archivo desde tu carpeta o el escritorio y lo sueltas directo sobre el botón, sin tener que abrir el selector.",
      "Vista previa dentro del app: clic en el ojo de cualquier archivo abre el documento en una ventana flotante con visor de PDF e imágenes. Sin pestañas nuevas, sin descargar al disco.",
      "Sección Documentos del venue rediseñada: agrega cuantos archivos quieras con el nombre que tú elijas. Antes eran 4 slots fijos (Press Kit, Reglamento, Planos, Contrato) — esos siguen como sugerencias para llenar rápido. El homesite muestra los nombres que tú pusiste.",
      "Nueva sección Tipos de archivo en Configuración: además de los predeterminados (Cotización, Contrato, Comprobante, Recibo, Otro), agregas los tipos propios de tu venue — Factura, Voucher, Acta, lo que uses. Al subir un archivo, si eliges Otro puedes especificar el nombre y guardarlo como tipo nuevo del venue para futuras subidas.",
    ],
  },
  {
    date: "2026-06-04",
    title: "Cuentas, Depósitos en garantía del cliente y Proveedores rediseñados",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "La sección antes llamada Pagos ahora se llama Cuentas, con pestañas separadas: Por cobrar, Pagos, Reembolsos y la nueva Depósitos en garantía.",
      "El default en Pagos y Reembolsos ahora es vista anual, y los registros más recientes salen primero.",
      "Nueva pestaña Depósitos en garantía dentro de Cuentas: arriba ves el total en custodia (lo que estás reteniendo entre cliente y proveedores), contadores de cada estado, chips para filtrar (Todos / En custodia / Devueltos / Aplicados) y dos tablas separadas — por evento y por proveedor — con una columna de devolución que muestra fecha, monto y aviso si fue parcial.",
      "Nueva sub-pestaña Depósitos en garantía dentro de cada evento. Aquí registras el depósito que cobras al cliente como garantía, marcas devoluciones (incluyendo parciales), aplicas por daños o penalización, reactivas si te equivocaste y eliminas. Todo queda en bitácora.",
      "Vocabulario unificado: lo que antes decía Cobrado en depósitos de proveedor ahora dice Aplicado — describe mejor lo que pasa cuando el venue se queda con el dinero. Los depósitos viejos siguen siendo legibles sin que tengas que hacer nada.",
      "Página del proveedor rediseñada con tarjeta de identidad: avatar con la inicial, nombre, categoría, badges, chips clicables a Instagram y sitio web, y un strip de estadísticas (Rating, Eventos, Contactos, Reseñas).",
      "El Resumen del proveedor ahora abre en modo lectura — ves la información limpia como fichas. Botón Editar para entrar al modo edición cuando lo necesites.",
      "Eliminar proveedor ahora es Archivar: el proveedor deja de aparecer en el directorio y en los selectores de eventos nuevos, pero los eventos viejos que ya lo tenían asignado siguen mostrándolo, y sus depósitos y reseñas se conservan. Puedes desarchivarlo cuando quieras desde su página, y hay un filtro Archivados en el directorio para verlos.",
      "Directorio de proveedores con pestañas separadas (Directorio y Depósitos en garantía) y filtro de estado en chips con contadores por cada estado.",
      "Precio acordado ahora es editable directamente desde el modo edición del evento (antes solo desde la pestaña Cotización), y aparece como badge \"Precio acordado: $X\" en la pestaña Pagos del evento.",
    ],
  },
  {
    date: "2026-06-04",
    title: "Setup de venue rediseñado",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "La página de edición de un venue está reorganizada en grupos (Identidad, Espacio, Servicios, Logística) con un menú lateral pegado que te lleva a cada sección y muestra una palomita verde cuando esa sección ya tiene datos. Saber qué falta es un vistazo.",
      "Nueva tarjeta de encabezado con la foto del venue, nombre, dirección, status público y una barra de progreso con cuántas secciones tienes completas sobre el total.",
      "Los cuatro bloques de Servicios (Incluidos, Opcionales, Exclusivos, Externos) ahora viven en una sola sección con tabs y contador por cada uno. Cada tab tiene un ícono ⓘ que al pasar el mouse explica qué significa — la misma definición que ve el visitante en venueboard.com.",
      "Barra de guardar fija abajo: aparece sola cuando hay cambios sin guardar, con botones Guardar y Descartar. Si intentás cerrar la pestaña o salir a otra página sin guardar, el navegador te avisa.",
      "Si manejas más de un venue, arriba aparece un link 'Volver a venues' para regresar al listado sin tocar el menú lateral.",
    ],
  },
  {
    date: "2026-06-04",
    title: "Venues: buscador, filtros y tabla rediseñada",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "La página de venues ahora tiene buscador por nombre o dirección y filtros por zona, tipo y status. Útil si manejas varios venues a la vez.",
      "La tabla se rediseñó al estilo Venueboard: tarjeta redondeada con sombra, encabezados grises en mayúsculas, hover suave y verde brand en el toggle de vista. Cada fila muestra thumbnail, nombre con dirección debajo, zona, tipo, capacidad y el badge de status.",
      "La vista de tabla pasa a ser el default y arriba aparece un contador con el total filtrado vs. total y un atajo para limpiar filtros.",
      "Fix: en la página de un venue, el bloque 'Servicios Incluidos' decía 'Selecciona los servicios opcionales'. Ahora dice 'Selecciona los servicios incluidos'.",
    ],
  },
  {
    date: "2026-06-01",
    title:
      "Homesite: notificaciones, comentarios, recuperar contraseña y calendario",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Los toasts del homesite ahora usan el mismo estilo Venueboard que el app: fondo blanco con franja del color de severidad, icono por tipo, verde brand para éxito y barra de progreso al cerrarse.",
      "Formulario /feedback rediseñado con los componentes del proyecto (Select, Textarea, Fieldset). Cada comentario ahora dispara mail a info@venueboard.com y notificación a Telegram (template Brevo 14), y la entrada Feedback del sidebar admin muestra un badge con la cantidad de comentarios pendientes en tiempo real.",
      "Pantallas de Recuperar y Restablecer contraseña migradas al mismo estilo de Login/Signup: tarjeta blanca con backdrop blur, iconos decorativos, brand green #1A4D2E y botón pill. Estados de éxito/error usan el toast nuevo.",
      "Calendario del venue público: ahora la grilla del mes aparece de entrada con shimmer en cada celda mientras llegan los eventos, en lugar del texto 'Loading events...'. Sin CLS cuando llega la data.",
    ],
  },
  {
    date: "2026-05-26",
    title: "Pipeline de leads: scroll, colapso de columnas y pulido visual",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "El scroll horizontal del kanban ahora funciona bien en pantallas grandes: cuando hay muchas columnas (5+) las cards mantienen un ancho legible y aparece scrollbar abajo, con gradientes en los bordes que avisan que hay más contenido al costado.",
      "Las columnas vacías ahora se colapsan automáticamente a un strip vertical de 48px con el nombre rotado — así no comen espacio. Click en el strip para expandir.",
      "Podés colapsar cualquier columna manualmente con el botón « del header. El estado de colapso se guarda por venue (cambiar de venue no afecta otro).",
      "Mientras arrastrás una card, todas las columnas se expanden automáticamente para que puedas soltarla en una que estaba colapsada.",
      "El kanban ahora usa todo el ancho de la pantalla (antes estaba limitado a 1152px). Calendario y Estadísticas siguen capeados porque no lo necesitan.",
      "Cada columna muestra el valor total acumulado de sus leads (suma del desglose de cotizaciones) al lado del contador — útil en Ganados para ver pipeline value de un vistazo.",
      "Las cards de lead ahora tienen una franja izquierda más visible (4px en lugar de 2px) y los headers de columna están en mayúsculas tipo CRM moderno.",
    ],
  },
  {
    date: "2026-05-26",
    title: "Notificaciones rediseñadas",
    badge: { label: "Mejora", color: "blue" },
    items: [
      "Los toasts que aparecen arriba a la derecha (lead creado, evento guardado, error al borrar, etc.) ahora siguen el estilo del resto de la app: fondo blanco con franja del color de la severidad, icono ilustrativo por tipo (check, X, triángulo, info) y tipografía consistente.",
      "Verde brand de Venueboard para los mensajes de éxito en lugar del verde Tailwind genérico, dark mode soportado correctamente, y una barra de progreso sutil en el borde inferior que muestra cuánto falta para que se cierre solo.",
      "Si hay varios toasts apilados, los espacios entre ellos dejan pasar el click al contenido de abajo — antes el área completa de la pila los bloqueaba.",
    ],
  },
  {
    date: "2026-05-26",
    title: "Leads: ahora son una página completa con pestañas",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Los leads ahora abren en su propia página completa (antes era un panel lateral chico) con pestañas General, Fechas, Timeline y Cotización al estilo de la página de evento. Toda la información y las acciones (guardar, convertir, eliminar) caben sin pelear con el espacio.",
      "Al volver al pipeline desde la página de un lead, la tarjeta de ese lead se resalta y la columna scrollea hasta ella para que no pierdas tu lugar.",
    ],
  },
  {
    date: "2026-05-26",
    title:
      "Timeline de seguimientos: notas + actividad automática en un solo hilo",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva pestaña Timeline en cada lead y nueva pestaña Seguimientos en cada evento. Mezclan en orden cronológico las notas que vas agregando con todos los movimientos automáticos: lead creado, estado cambiado, fecha agregada, cotización generada, pago registrado, evento cancelado, proveedor asignado, etc.",
      "Las notas ahora son un hilo: cada una guarda quién la escribió, fecha y hora, se pueden editar o eliminar, y aparecen una debajo de la otra como un chat de CRM. Reemplaza al campo único de notas que se sobrescribía cada vez que alguien guardaba.",
      "Al convertir un lead en evento, todo el historial y los seguimientos se quedan visibles también desde el evento — las notas que escribiste antes de convertir siguen ahí, y las nuevas que agregas desde el evento aparecen en los dos lados.",
      "Cada movimiento muestra detalles ricos: 'Pago registrado · $5,000 · transferencia', 'Precio editado · $50,000 → $75,000', 'Estado cambiado · pendiente → en atención', 'Proveedor asignado · DJ Audio (música)', etc.",
      "Eventos que se crearon manualmente (sin venir de un lead) ahora también tienen su propio hilo de notas independiente.",
      "Bitácora: nuevas categorías 'Nota de lead' y 'Nota de evento' visibles en los filtros, con badges de color distintos para distinguirlas de un vistazo.",
    ],
  },
  {
    date: "2026-05-26",
    title: "Historial previo importado al timeline",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Las notas que ya tenían tus leads y eventos antes de esta actualización quedaron importadas como primera entrada del hilo de Timeline / Seguimientos, con autor 'Sistema (importado)'. No se perdió nada del historial previo — la primera vez que abras un lead o evento viejo vas a ver su nota original arriba del hilo.",
    ],
  },
  {
    date: "2026-05-26",
    title: "Bitácora: filtros más útiles",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nuevo filtro por Acción con todas las acciones agrupadas por tipo (Eventos, Pagos, Leads, Proveedores, etc.) para encontrar rápido un registro específico.",
      "Nuevo filtro por Nombre que busca al mismo tiempo en el usuario que ejecutó la acción y en el nombre del registro afectado.",
      "Quitamos el filtro de Entidad — el filtro de Acción lo reemplaza con más precisión.",
    ],
  },
  {
    date: "2026-05-26",
    title: "Proveedores: Instagram y enlaces rápidos",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nuevo campo Instagram en cada proveedor, editable desde su página y desde el formulario de alta.",
      "En la lista de proveedores, los íconos de Instagram (rosa) y sitio web (azul) se vuelven clicables cuando hay un enlace guardado, para abrirlos al instante en una pestaña nueva.",
    ],
  },
  {
    date: "2026-05-20",
    title: "Estadísticas de leads",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva pestaña Estadísticas en Leads (junto a Pipeline y Calendario) con vista por venue: KPIs de total, ganados, perdidos, tasa de cierre, conversión a evento y cotizaciones generadas.",
      "Gráficas de leads creados por día o mes según el rango, embudo por estado, distribución por origen y top 5 de temas.",
      "Selector de rango con 30 días, mes pasado, 90 días, 12 meses, año en curso y todo el tiempo. Se recuerda por venue para la próxima visita.",
      "Descarga de reporte ejecutivo en PDF (resumen, origen, tendencia y embudo) listo para mandar.",
      "Exportación de leads a CSV con los datos crudos del rango activo, incluyendo nombre, contacto, mensaje, fechas de interés y si el lead se convirtió en evento.",
      "Las descargas quedan registradas en la bitácora del venue para tener trazabilidad de quién bajó qué.",
    ],
  },
  {
    date: "2026-05-11",
    title: "Proveedores: directorio, planners y depósitos en garantía",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva sección Proveedores: directorio interno por venue con contactos, notas en formato bitácora, reseñas del desempeño general y registro de eventos asignados. Categorías y subcategorías editables desde Admin.",
      "Planner como rol especial: cada evento tiene su tarjeta dedicada. Asígnalo desde tu directorio o créalo en el momento sin salir de la página del evento.",
      "Depósitos en garantía por proveedor: registra el dinero retenido y dale seguimiento con estados claros (En custodia, Devuelto, Cobrado), incluyendo devoluciones parciales y opción de reactivar si te equivocaste.",
      "Vista global de depósitos en /dashboard/proveedores: cuánto está en custodia, cuánto devolviste y cuánto cobraste de un vistazo, con filtros por estado y proveedor.",
      "Comentarios por evento (ej. 'Flores 1 hace centros de mesa, Flores 2 decoración general') visibles directamente en la página del evento, separados de las reseñas generales del proveedor.",
      "Navegación inteligente: al ir del evento al proveedor (o viceversa), el botón Volver te regresa exactamente a la pestaña en la que estabas.",
      "Cada acción (crear, asignar, depositar, devolver, cobrar, comentar, reseñar) queda registrada en la bitácora con el proveedor y evento involucrados.",
    ],
  },
  {
    date: "2026-05-11",
    title: "Corrección de zona horaria en pagos y reembolsos",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Corregido el bug por el que un pago o reembolso registrado un día (ej. 11 de mayo) se guardaba como el día anterior por la zona horaria del venue. Los registros nuevos ya se guardan en el día correcto.",
      "Los registros antiguos que quedaron desfasados ahora se muestran en el día que originalmente se eligió.",
      "El dashboard de Pagos y Reembolsos (filtros por mes/año, gráficas mensuales y desglose anual) ahora usa la zona horaria del venue, por lo que cada pago aparece en el mes que le corresponde.",
      "Formato de fecha unificado en las tablas a estilo '5 de abril de 2026' para evitar la inconsistencia entre dd/mm/yyyy y mm/dd/yyyy.",
    ],
  },
  {
    date: "2026-05-11",
    title: "Corrección en validación del desglose con IVA",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Corregido el aviso de la sección de pagos que mostraba que el total con IVA no coincidía con el precio acordado aun cuando los montos eran iguales en pantalla. La comparación ahora tolera diferencias por redondeo de centavos.",
    ],
  },
  {
    date: "2026-05-03",
    title: "Ranking dinámico de venues en venueboard.com",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Los venues más activos en la plataforma ahora aparecen primero en los resultados de búsqueda de venueboard.com.",
      "Mantener tu venue al día mejora directamente tu visibilidad ante prospectos.",
    ],
  },
  {
    date: "2026-04-28",
    title: "Tu calendario ahora es buscable en venueboard.com",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Los prospectos ya pueden buscar venues por disponibilidad de fecha directamente en venueboard.com.",
      "Mantén tu calendario al día: cada bloqueo, lead o evento confirmado se refleja en los resultados de búsqueda para que solo te contacten cuando estás disponible.",
    ],
  },
  {
    date: "2026-04-06",
    title: "¡Tenemos blog!",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Arrancamos el blog de Venueboard. Pronto encontrarás ideas, guías y tendencias para planear tu próximo evento.",
      "Disponible en español e inglés en venueboard.com/blog.",
    ],
  },
  {
    date: "2026-03-03",
    title: "Rediseño de la página de Leads",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Header rediseñado al estilo de la página de Inicio: título, subtítulo con nombre del venue y pills de navegación.",
      "Nueva pestaña Calendario: vista anual de 12 meses mostrando fechas de interés de leads (ámbar) y eventos confirmados (rojo) de un vistazo.",
      "Celdas con degradado diagonal cuando una fecha tiene tanto leads interesados como eventos confirmados.",
      "Badge numérico en días con 2+ leads interesados para identificar fechas disputadas.",
      "Navegación por año con flechas para explorar fechas pasadas y futuras.",
      "Popover al pasar el cursor o hacer clic en un día: muestra los leads interesados con su estado y los eventos confirmados con nombre.",
      "Clic en un lead del popover abre directamente el panel lateral con todos sus detalles.",
      "Botones de acción (Crear lead, Cancelar, Guardar) unificados con el estilo ligero usado en la página de Inicio.",
    ],
  },
  {
    date: "2026-03-03",
    title: "Leads: Pipeline completo de ventas",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Panel lateral deslizable para ver y editar leads: todos los campos editables directamente (nombre, teléfono, email, tema, notas, origen).",
      "Fechas de interés: agrega múltiples rangos de fechas por lead con selectores de fecha tipo dropdown (día, mes, año, hora, minuto).",
      "Detección de choques en tiempo real: alerta roja para fechas ocupadas por eventos y alerta ámbar mostrando nombres de otros leads interesados.",
      "Cotización en leads: tabla de desglose con conceptos, unidades, precio unitario, toggle de IVA y generación de PDF con folio COT-L.",
      "Historial de cotizaciones por lead con folio, fecha, usuario y opción de marcar como enviada.",
      "Agregar conceptos rápidamente desde el desglose sin ir a configuración.",
      "Conversión de lead a evento: selecciona la fecha de interés, copia el desglose opcionalmente y crea el evento con un clic.",
      "Fechas con choques contra eventos existentes se bloquean en el diálogo de conversión.",
      "Leads convertidos se mueven a la columna Ganados y quedan bloqueados (no se pueden arrastrar ni editar).",
      "Banner en lead convertido muestra estado del evento vinculado (activo o cancelado) con enlace directo.",
      "Fecha del evento vinculado se muestra en verde sobre las fechas de interés originales.",
      "Tarjetas del Kanban rediseñadas: nombre completo, tema, pills de fechas, total de desglose, cotizaciones y origen.",
      "Columnas del sistema (Pendiente, Ganados, Perdidos) protegidas: no se pueden eliminar ni renombrar.",
      "Reordenamiento de columnas con botones arriba/abajo desde la configuración.",
    ],
  },
  {
    date: "2026-03-03",
    title: "Mejoras en edición de fechas",
    badge: { label: "Mejoras", color: "emerald" },
    items: [
      "Selectores de fecha tipo dropdown (día, mes, año, hora, minuto) reemplazan el campo datetime-local en la edición de eventos y en fechas de interés de leads.",
      "Validación de secuencia completa en eventos: inicio bloqueo ≤ montaje ≤ evento ≤ fin evento ≤ desmontaje ≤ fin bloqueo.",
      "Ajuste automático de fechas intermedias al editar solo inicio/fin del bloqueo (cuando las fechas avanzadas están ocultas).",
      "Detección de choques en tiempo real mientras editas las fechas de un evento, antes de guardar.",
      "Botón de guardar bloqueado cuando las fechas están en orden inválido.",
      "Toggle 'Editar fechas avanzadas' disponible en modo edición para controlar montaje y evento por separado.",
    ],
  },
  {
    date: "2026-03-03",
    title: "Rediseño del Calendario",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Controles rediseñados: navegación con flechas, título del mes, botón 'Hoy' y pills de vista (Semana/Mes/Año) y filtro por tipo de evento.",
      "Botón 'Nuevo Evento' integrado directamente en la barra de controles del calendario.",
      "Grilla del calendario envuelta en tarjeta con bordes redondeados, sombras y soporte completo para modo oscuro.",
      "Eventos con color de acento (#1b4d2e), chips redondeados con efecto hover y enlace '+N más' estilizado.",
      "Vista de semana con etiquetas de hora limpias e indicador de hora actual en color de acento.",
      "Día actual resaltado con círculo de acento y texto blanco.",
      "Vista anual: tarjetas de mes con encabezado de acento, efecto hover con elevación, indicadores de ocupación en rojo y anillo de acento en el día actual.",
      "Click en cualquier mes de la vista anual navega directamente a ese mes en la vista mensual.",
      "Filtro de tipo de evento deshabilitado visualmente en la vista anual ya que solo muestra eventos y bloqueos.",
    ],
  },
  {
    date: "2026-02-28",
    title: "Cotizaciones en PDF",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Genera cotizaciones profesionales en PDF desde la página de cualquier evento con un solo clic.",
      "Diseño A4 completo con encabezado, datos del cliente, datos del evento, desglose de conceptos, totales e IVA.",
      "Descuentos mostrados con formato estándar mexicano: entre paréntesis y separados del subtotal.",
      "Historial de cotizaciones generadas por evento con folio, fecha, usuario y estado de envío.",
      "Personalización en Configuración → Cotizaciones: colores de barras, acentos y separadores, logo con tamaño ajustable, altura de encabezado, nombre personalizado y más.",
      "Vista previa en tiempo real del PDF mientras configuras el diseño.",
      "Tier Premium: colores, logo, dirección, contacto, vigencia, términos personalizados y sin marca de agua. Los usuarios gratuitos pueden configurar todo y ver cómo se vería con Premium.",
    ],
  },
  {
    date: "2026-02-28",
    title: "Rediseño de la página de evento",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Nuevo layout: Horarios, Cotización y Pagos ahora aparecen primero para acceso inmediato a la información más importante.",
      "Resumen financiero en el header: precio acordado, pagado y pendiente visibles sin hacer scroll.",
      "Información del cliente integrada en el header como referencia compacta en lugar de ocupar media pantalla.",
      "Barra de acciones (Guardar, Cancelar, Eliminar) fija en la parte superior durante la edición para que siempre esté visible.",
      "Bitácora más detallada: al editar un evento se registran los campos específicos que cambiaron (ej. RFC, Vendedor, etc.).",
    ],
  },
  {
    date: "2026-02-28",
    title: "Vista de tabla en Eventos",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva vista de tabla en la lista de eventos: alterna entre tarjetas y tabla desde el filtro.",
      "La tabla muestra nombre, tipo, estado, fecha de inicio y fecha de fin con columnas responsivas.",
      "Tu vista preferida se guarda automáticamente para futuras visitas.",
    ],
  },
  {
    date: "2026-02-28",
    title: "Rediseño de la página de Inicio",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Página de Inicio rediseñada con el mismo estilo visual que Pagos: tarjetas con icono, bordes redondeados y soporte para modo oscuro.",
      "Navegación por pestañas: 'Resumen de eventos' y 'Analíticas web' para separar la información.",
      "Saludo dinámico según la hora del día con mensajes variados.",
      "KPIs de eventos con tooltips informativos: eventos por venir, eventos este año, total cobrado (pagos del año) y por cobrar (eventos próximos).",
      "Gráfica comparativa de eventos por mes con vista de tres años.",
      "Resumen de eventos por año con conteo total.",
      "Solo visible para owners y admins (viewers ven directamente las analíticas web).",
    ],
  },
  {
    date: "2026-02-27",
    title: "Descuentos e IVA en cotización",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nuevo tipo de concepto 'Descuento': al seleccionarlo en el desglose, el precio unitario se aplica automáticamente como negativo.",
      "Los conceptos de tipo descuento se identifican con un badge rojo en la tabla de conceptos y en el selector del desglose.",
      "Nuevo toggle 'Incluir IVA (16%)' por evento: calcula automáticamente el IVA sobre el subtotal del desglose.",
      "La validación del desglose contra el precio acordado ahora considera el total con IVA cuando está activado.",
    ],
  },
  {
    date: "2026-02-27",
    title: "Métodos de pago configurables",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva sección 'Métodos de pago' en Configuración: agrega y elimina métodos de pago personalizados para tu venue.",
      "Los métodos de pago por defecto (Efectivo y Transferencia) se crean automáticamente al entrar por primera vez.",
      "Selector de método de pago en el registro de pagos y reembolsos con opción de crear nuevos métodos sobre la marcha.",
      "Homogenización de métodos de pago: al editar un pago o reembolso existente, el método se selecciona desde un dropdown en lugar de texto libre.",
    ],
  },
  {
    date: "2026-02-27",
    title: "Bitácora de actividad",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva sección Bitácora: registro automático de todas las acciones realizadas en tu venue (eventos, pagos, reembolsos, leads, conceptos y configuración).",
      "Filtros por tipo de entidad y rango de fechas para encontrar rápidamente cualquier acción.",
      "Paginación con botón 'Cargar más' para navegar el historial completo.",
    ],
  },
  {
    date: "2026-02-27",
    title: "Cuentas por cobrar",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nueva pestaña 'Por cobrar' en el dashboard de Pagos: visualiza el saldo pendiente de cada evento con precio acordado.",
      "Resumen ejecutivo con total comprometido, cobrado y por cobrar, con avance porcentual por evento.",
      "Indicador de días restantes para cada evento con código de color según urgencia.",
      "Filtro rápido para alternar entre eventos pendientes de pago y el historial completo.",
    ],
  },
  {
    date: "2026-02-18",
    title: "Sistema de cancelación y reembolsos",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Nuevo flujo completo para cancelar eventos: registra la razón, el monto reembolsado y marca el evento como cancelado.",
      "Gestión de reembolsos por evento: agrega, elimina y consulta reembolsos con monto, método de pago y notas.",
      "Reactivación de eventos cancelados con eliminación automática de reembolsos y advertencia de cruces de fechas.",
      "Los eventos cancelados se muestran en el calendario con estilo tachado y opacidad reducida.",
      "Controles de pago deshabilitados en eventos cancelados para evitar modificaciones accidentales.",
      "Eliminación atómica: al borrar un evento se eliminan también todos sus pagos y reembolsos en una sola operación.",
    ],
  },
  {
    date: "2026-02-18",
    title: "Rediseño del dashboard de pagos",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Dashboard de pagos rediseñado con tarjetas de resumen, gráfica de ingresos mensuales y tabla de pagos detallada.",
      "Nueva vista anual con navegación por año y gráfica de barras de ingresos por mes.",
      "Celdas editables inline para monto, método de pago y notas directamente desde la tabla de pagos.",
      "Nuevo badge 'Sin cotizar' para eventos sin precio acordado, visible en la lista de eventos y en el detalle.",
    ],
  },
  {
    date: "2026-02-18",
    title: "Correcciones y mejoras de estabilidad",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Corregida inconsistencia en el badge de pago: eventos sin precio acordado ya no se marcan como 'Pagado'.",
      "Mejoras en el manejo de errores al guardar pagos, agregar reembolsos y enriquecer datos del dashboard.",
    ],
  },
  {
    date: "2026-02-17",
    title: "Notificación de nueva versión",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Al publicar una nueva versión, los usuarios con la app abierta reciben un aviso para recargar la página.",
      "La verificación se realiza automáticamente cada 5 minutos y al volver a la pestaña.",
    ],
  },
  {
    date: "2026-02-17",
    title: "Ajuste automático de fechas en eventos",
    badge: { label: "Corrección", color: "red" },
    items: [
      "Las fechas intermedias (montaje, evento, desmontaje) ahora se ajustan automáticamente al cambiar el inicio o fin del bloqueo.",
      "En modo avanzado, las fechas se reordenan dinámicamente para que nunca queden en un orden inválido.",
      "Al intentar colocar el fin del bloqueo antes del inicio, se muestra una alerta y se revierte el cambio.",
      "El checkbox de horarios avanzados ahora siempre inicia desactivado al abrir el diálogo.",
    ],
  },
  {
    date: "2026-02-12",
    title: "Mapa de venues en búsqueda",
    badge: { label: "Nuevo", color: "purple" },
    items: [
      "Implementamos un mapa interactivo en la búsqueda de venues para que puedas explorar ubicaciones de forma visual.",
    ],
  },
  {
    date: "2026-02-12",
    title: "Nuevo perfil de usuario",
    badge: { label: "Nuevo", color: "blue" },
    items: [
      "Nueva página Mi Perfil accesible desde el menú de usuario para consultar y editar tu información personal.",
      "Campos editables: nombre, apellido y teléfono. Email y rol se muestran solo en modo lectura.",
      "Reglas de seguridad actualizadas para que los usuarios solo puedan modificar sus propios datos permitidos.",
    ],
  },
  {
    date: "2026-02-12",
    title: "Rediseño completo de la experiencia de eventos",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Página de detalle de evento rediseñada con layout de tarjetas en múltiples columnas (Horarios, Cliente, Notas, Cotización, Pagos).",
      "Lista de eventos rediseñada con tarjetas, badges de tipo y estado de pago, y filtros en una tarjeta compacta.",
      "Separador visual entre eventos pasados y próximos al activar la vista de eventos pasados.",
      "Diálogo de detalle de evento del calendario rediseñado con header de acento, badges de tipo y edición inline de nombre y notas.",
      "Diálogo de nuevo evento rediseñado con secciones colapsables, selectores de hora en formato 24h y toggle de tipo de cliente.",
      "Nombres de eventos largos ahora se muestran completos con guiones en español en lugar de truncarse.",
    ],
  },
  {
    date: "2026-02-12",
    title: "Validaciones y detección de cruces de fechas",
    badge: { label: "Mejoras", color: "emerald" },
    items: [
      "Detección de cruces de fechas al crear o editar eventos: se muestra una advertencia con los eventos que se solapan.",
      "Las advertencias de cruce ahora indican el tipo de evento (Evento, Bloqueo, Reunión, Scouting) junto al nombre.",
      "Validación de campos obligatorios al crear eventos: nombre no vacío, fechas en orden correcto.",
      "Confirmación antes de guardar cambios cuando hay cruces de fechas detectados.",
      "Corrección: el campo Contacto ahora se muestra correctamente en eventos de tipo Reunión y Scouting.",
    ],
  },
  {
    date: "2026-02-10",
    title: "Correcciones y mejoras generales",
    badge: { label: "Correcciones", color: "emerald" },
    items: [
      "Los tipos de venue ahora se cargan desde los datos del sistema en lugar de estar fijos.",
      "Drag & drop del tablero Kanban de Leads corregido: las tarjetas ahora se arrastran correctamente.",
      "Hover de la barra lateral ahora usa el color de acento de la plataforma en lugar de gris.",
      "Registrar un pago en un evento ahora se refleja al instante sin necesidad de recargar la página.",
    ],
  },
  {
    date: "2026-02-10",
    title: "Corrección visual en Leads",
    badge: { label: "Leads", color: "emerald" },
    items: [
      "Los colores de las columnas del tablero Kanban ahora se muestran correctamente en la versión publicada.",
    ],
  },
  {
    date: "2026-02-10",
    title: "Mejoras en edición de venues",
    badge: { label: "Venues", color: "emerald" },
    items: [
      "Nuevo diseño de secciones colapsables con identidad visual de acento y soporte para modo oscuro.",
      "Checkboxes y switches ahora usan el color de acento de la plataforma.",
      "Los textos de los checkboxes ahora son clickeables para activar o desactivar la opción.",
      "Redes sociales simplificadas: solo ingresa tu usuario de Instagram y tu número de WhatsApp, la plataforma genera los enlaces automáticamente.",
      "Nuevas áreas adicionales disponibles: Área de fumadores, Cuarto de apoyo y Patio interior.",
      "Vista de lista de venues con opción de tabla y redirección automática cuando solo tienes un venue.",
    ],
  },
  {
    date: "2026-02-10",
    title: "Nuevo tablero Kanban de Leads",
    badge: { label: "Leads", color: "emerald" },
    items: [
      "Nueva vista Kanban para gestionar leads: arrastra y suelta tarjetas entre columnas para cambiar su estado.",
      "Crea leads manualmente con nombre, teléfono, email, tema, notas y origen personalizado.",
      "Los leads del sitio web aparecen automáticamente en la columna Pendiente con su mensaje original.",
      "Notas editables en cada lead: agrega y actualiza notas desde el modal de detalle.",
      "Columnas personalizables: renombra, cambia colores, oculta o agrega nuevas columnas desde la configuración.",
      "Menú de 3 puntos en cada tarjeta con opciones de ver/editar, mover a cualquier columna y eliminar.",
      "Eliminación suave con razón: los leads eliminados se marcan como perdidos sin borrarse permanentemente.",
      "Orígenes con colores automáticos y autocompletado basado en orígenes existentes.",
      "Selector de país (MX/US) integrado en el campo de teléfono al crear leads.",
      "Datos en tiempo real: los cambios de otros usuarios se reflejan al instante en el tablero.",
    ],
  },
  {
    date: "2026-02-09",
    title: "Nueva identidad visual y mejoras en navegación",
    badge: { label: "Diseño", color: "emerald" },
    items: [
      "Nueva identidad visual con color de acento en la barra lateral, navegación y página de novedades.",
      "El elemento activo del menú ahora se resalta con un fondo verde para mejor visibilidad.",
      "Página de Novedades rediseñada con tarjetas, pestañas y una experiencia más clara.",
      "Pestañas de Novedades, En Desarrollo y Problemas Conocidos para mejor organización.",
      "Dashboard de inicio actualizado con la nueva paleta de colores en tarjetas de estadísticas, gráficas y controles.",
      "Página de Ayuda rediseñada con tarjetas, iconos y una experiencia más limpia para enviar y consultar tickets de soporte.",
    ],
  },
  {
    date: "2026-02-08",
    title: "Selector global de venue y datos en tiempo real",
    badge: { label: "Plataforma", color: "emerald" },
    items: [
      "Nuevo selector global de venue: cambia de venue desde la barra lateral y se refleja en toda la plataforma al instante.",
      "Información en tiempo real: eventos, pagos y más se actualizan automáticamente sin recargar la página.",
      "Tu venue seleccionado se recuerda entre sesiones para una experiencia más fluida.",
    ],
  },
  {
    date: "2025-06-14",
    title: "Mejoras avanzadas en gestión de eventos y clientes",
    badge: { label: "Eventos", color: "purple" },
    items: [
      "Nombre de evento visible en el calendario y mejoras en la vista general de eventos.",
      "Redirección automática a la página del evento tras crearlo.",
      "Nueva vista anual del calendario.",
      "Renovación completa de la vista de evento y cliente.",
      "Soporte para conceptos y desglose de cotizacion de eventos.",
      "Soporte de pagos por evento.",
      "Nueva sección de pagos por evento y pantalla de pagos mensuales en el Dashboard.",
      "Modelo de datos unificado: nombre, apellido, tipo de persona, empresa (solo moral), correo, teléfono, RFC, vendedor y # de personas.",
      "Edición y visualización de todos los campos de cliente con validaciones y estilos consistentes.",
      "Selector estilizado para tipo de persona (física/moral) y lógica condicional para empresa.",
      "Compatibilidad con eventos antiguos y corrección de bugs menores.",
    ],
  },
  {
    date: "2025-06-11",
    title: "Novedades para Usuarios",
    badge: { label: "Mejoras", color: "blue" },
    items: [
      "Mejoras en la navegación del dashboard: ahora es más fácil encontrar y acceder a tus formularios y eventos.",
      "El formulario de contacto ahora muestra mensajes claros de éxito o error al enviar.",
      "Puedes ver el estado actualizado de tus solicitudes y formularios en tiempo real.",
      "La visualización de mensajes largos y detalles de formularios es más cómoda gracias a los nuevos diálogos y botones de expansión.",
      "La tabla de formularios es más legible y se adapta mejor a pantallas pequeñas.",
      "Se han corregido errores menores y mejorado el rendimiento general de la aplicación.",
      "Se agrega la capacidad de agregar links a las redes sociales de los venues.",
    ],
  },
  {
    date: "2025-06-10",
    title: "Mejoras en el sistema de Soporte",
    badge: { label: "Soporte", color: "emerald" },
    items: [
      "Ahora puedes ver el estado de tus tickets de soporte en español (Abierto, En Atención, Cerrado).",
      "Cuando envías un ticket, recibirás una notificación de éxito o error.",
      'Después de enviar un ticket, este aparece inmediatamente en la lista de "Tickets Activos".',
      "Puedes ver la fecha de creación y la última actualización de cada ticket.",
      "Si un administrador deja un comentario en tu ticket, ahora puedes verlo destacado en el ticket.",
      "El formulario de envío de tickets ahora tiene un selector de asunto con opciones comunes y la posibilidad de especificar otro asunto.",
      "La interfaz de soporte ha sido mejorada para mayor claridad y consistencia visual.",
    ],
  },
];

const inDevelopmentEntries = [
  {
    title: "Recordatorios",
    description:
      "Agenda pagos futuros y crea recordatorios por venue o por evento, todos visibles en una pestaña Recordatorios con filtros por fecha, tipo y estado.",
  },
  {
    title: "Notificaciones en tiempo real",
    description:
      "Alertas push para cambios en eventos, pagos recibidos y tickets de soporte.",
  },
  {
    title: "Exportación de datos",
    description:
      "Exporta eventos, clientes y pagos a Excel y PDF desde cualquier tabla.",
  },
];

const knownIssuesEntries = [
  {
    title: "Carga lenta en calendarios con muchos eventos",
    description:
      "En venues con más de 200 eventos por mes, la vista de calendario puede tardar algunos segundos en cargar.",
    status: "Investigando",
  },
  {
    title: "Formato de fecha en algunos navegadores",
    description:
      "En ciertos navegadores, las fechas pueden mostrar un formato distinto al esperado. Estamos trabajando en normalizarlo.",
    status: "En progreso",
  },
];

function formatDate(dateStr) {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const tabs = [
  { name: "Novedades", icon: SparklesIcon },
  { name: "En desarrollo", icon: WrenchScrewdriverIcon },
  { name: "Problemas conocidos", icon: ExclamationTriangleIcon },
];

const MAX_NEW = 3;

export function getUnseenCount() {
  const lastSeen = localStorage.getItem(CHANGELOG_STORAGE_KEY);
  if (!lastSeen) return Math.min(MAX_NEW, changelogEntries.length);
  return Math.min(
    MAX_NEW,
    changelogEntries.filter((e) => e.date > lastSeen).length,
  );
}

// Cuántas viñetas se ven antes de cortar. Una entrada larga no puede
// estirar su columna el triple que la de al lado: en una rejilla eso se ve
// como un hueco, no como contenido. Se corta y se abre bajo demanda.
const VISTA_PREVIA = 4;

const NovedadCard = ({ entry, nuevo }) => {
  const [abierta, setAbierta] = useState(false);
  const total = entry.items.length;
  const visibles = abierta ? entry.items : entry.items.slice(0, VISTA_PREVIA);
  const restantes = total - visibles.length;

  return (
    <div
      className={`flex flex-col rounded-xl border p-5 shadow-sm transition hover:shadow-md dark:bg-zinc-800/50 ${
        nuevo
          ? "border-[#1b4d2e]/30 bg-[#1b4d2e]/[0.03] ring-1 ring-[#1b4d2e]/10 dark:border-[#1b4d2e]/40"
          : "border-zinc-200 bg-white dark:border-zinc-700/60"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: ACCENT + "1a" }}
        >
          <RocketLaunchIcon className="h-4 w-4" style={{ color: ACCENT }} />
        </div>
        <div className="flex-1 min-w-0">
          <Subheading level={2}>{entry.title}</Subheading>
        </div>
        {nuevo && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#1b4d2e] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            Nuevo
          </span>
        )}
        <Badge color={entry.badge.color}>{entry.badge.label}</Badge>
      </div>
      <time className="text-xs font-medium text-zinc-400 dark:text-zinc-500">
        {formatDate(entry.date)}
      </time>

      <ul className="mt-4 space-y-2.5">
        {visibles.map((item, i) => (
          <li key={i} className="flex gap-2.5">
            <CheckCircleIcon
              className="mt-0.5 h-4 w-4 shrink-0"
              style={{ color: ACCENT }}
            />
            <Text className="!mt-0">{item}</Text>
          </li>
        ))}
      </ul>

      {total > VISTA_PREVIA && (
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          className="mt-4 self-start text-xs font-semibold hover:underline"
          style={{ color: ACCENT }}
        >
          {abierta
            ? "Ver menos"
            : `Ver ${restantes} ${restantes === 1 ? "novedad más" : "novedades más"}`}
        </button>
      )}
    </div>
  );
};

const Changelog = () => {
  const [newDates, setNewDates] = useState(new Set());

  useEffect(() => {
    const stored = localStorage.getItem(CHANGELOG_STORAGE_KEY);
    // Determine which entries are "new" — cap at MAX_NEW most recent
    const unseen = stored
      ? changelogEntries.filter((e) => e.date > stored)
      : changelogEntries.slice(0, MAX_NEW);
    setNewDates(
      new Set(
        unseen
          .slice(0, MAX_NEW)
          .map((e, i) => `${e.date}-${changelogEntries.indexOf(e)}`),
      ),
    );
    // Mark as seen after a short delay so the user sees the badges
    const timer = setTimeout(() => {
      const latest = changelogEntries[0]?.date;
      if (latest) localStorage.setItem(CHANGELOG_STORAGE_KEY, latest);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const isNew = (date, idx) => newDates.has(`${date}-${idx}`);

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <SparklesIcon className="h-6 w-6" style={{ color: ACCENT }} />
        <Heading level={1}>Novedades</Heading>
      </div>
      <Text className="mb-8">
        Las últimas mejoras y actualizaciones de VenueBoard.
      </Text>

      {/* Tabs */}
      <TabGroup>
        <TabList className="flex gap-1 rounded-xl bg-zinc-100 p-1 dark:bg-zinc-800/60">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className="group flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-zinc-500 outline-none transition
                hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200
                data-[selected]:bg-[#1b4d2e] data-[selected]:text-white data-[selected]:shadow-sm
                data-[selected]:dark:bg-[#1b4d2e] data-[selected]:dark:text-white"
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.name}</span>
            </Tab>
          ))}
        </TabList>

        <TabPanels className="mt-8">
          {/* Novedades Panel */}
          <TabPanel>
            {/* Rejilla: dos columnas en pantalla grande, una en celular.
                `items-start` para que una tarjeta corta no se estire a la
                altura de su vecina. */}
            <div className="grid gap-4 md:grid-cols-2 items-start">
              {changelogEntries.map((entry, idx) => (
                <NovedadCard
                  key={`${entry.date}-${idx}`}
                  entry={entry}
                  nuevo={isNew(entry.date, idx)}
                />
              ))}
            </div>
          </TabPanel>

          {/* En desarrollo Panel */}
          <TabPanel>
            <div className="grid gap-4 md:grid-cols-2 items-start">
              {inDevelopmentEntries.map((entry) => (
                <div
                  key={entry.title}
                  className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-700/60 dark:bg-zinc-800/50"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: ACCENT + "1a" }}
                    >
                      <WrenchScrewdriverIcon
                        className="h-4 w-4"
                        style={{ color: ACCENT }}
                      />
                    </div>
                    <div>
                      <Subheading level={3}>{entry.title}</Subheading>
                      <Text className="mt-1">{entry.description}</Text>
                    </div>
                  </div>
                </div>
              ))}
              <div className="md:col-span-2 rounded-xl border border-dashed border-zinc-300 p-6 text-center dark:border-zinc-600">
                <Text>Hay más mejoras en camino. Mantente al tanto.</Text>
              </div>
            </div>
          </TabPanel>

          {/* Problemas conocidos Panel */}
          <TabPanel>
            <div className="grid gap-4 md:grid-cols-2 items-start">
              {knownIssuesEntries.map((entry) => (
                <div
                  key={entry.title}
                  className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-700/60 dark:bg-zinc-800/50"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-500/10">
                      <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Subheading level={3}>{entry.title}</Subheading>
                        <Badge color="amber">{entry.status}</Badge>
                      </div>
                      <Text className="mt-1">{entry.description}</Text>
                    </div>
                  </div>
                </div>
              ))}
              <div className="md:col-span-2 rounded-xl border border-dashed border-zinc-300 p-6 text-center dark:border-zinc-600">
                <Text>
                  Si encuentras algún problema, repórtalo en la sección de
                  Soporte.
                </Text>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  );
};

export default Changelog;
